import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWorkflows } from '../api/Workflows.client';
import { queryKeys } from '../api/queryKeys';
import { Workflow, SortConfig } from '../containers/WorkflowsPage/types';

export interface WorkflowFilters {
  accountId: null | string;
  assignedTo: null | string;
  type: null | string;
}

interface WorkflowContextType {
  myWorkflows: Workflow[];
  companyWorkflows: Workflow[];
  allWorkflows: Workflow[];
  loading: boolean;
  error: string | null;
  getWorkflowById: (id: string) => Workflow | undefined;
  refreshWorkflows: () => Promise<void>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  mySortConfig: SortConfig | null;
  setMySortConfig: (config: SortConfig | null) => void;
  companySortConfig: SortConfig | null;
  setCompanySortConfig: (config: SortConfig | null) => void;
  setFilters: (filters: WorkflowFilters) => void;
  filters: WorkflowFilters;
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(
  undefined
);

export const useWorkflowContext = () => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error(
      'useWorkflowContext must be used within a WorkflowProvider'
    );
  }
  return context;
};

interface WorkflowProviderProps {
  children: ReactNode;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<WorkflowFilters>({
    accountId: null,
    assignedTo: null,
    type: null,
  });
  const [mySortConfig, setMySortConfig] = useState<SortConfig | null>(null);
  const [companySortConfig, setCompanySortConfig] = useState<SortConfig | null>(
    null
  );

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryFn: fetchWorkflows,
    queryKey: queryKeys.workflows(),
  });

  const refreshWorkflows = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
  }, [queryClient]);

  const contextValue: WorkflowContextType = useMemo(() => {
    const myWorkflows = data?.myWorkflows ?? [];
    const companyWorkflows = data?.companyWorkflows ?? [];
    const allWorkflows = data?.allWorkflows ?? [];
    const error = queryError ? 'Failed to load workflows' : null;

    const getWorkflowById = (id: string): Workflow | undefined =>
      allWorkflows.find(
        (w) => w.workflowTblId === Number(id) || w.linkId === id
      );

    return {
      allWorkflows,
      companySortConfig,
      companyWorkflows,
      error,
      filters,
      getWorkflowById,
      loading,
      mySortConfig,
      myWorkflows,
      refreshWorkflows,
      searchTerm,
      setCompanySortConfig,
      setFilters,
      setMySortConfig,
      setSearchTerm,
    };
  }, [
    data,
    queryError,
    companySortConfig,
    mySortConfig,
    searchTerm,
    filters,
    loading,
    refreshWorkflows,
  ]);

  return (
    <WorkflowContext.Provider value={contextValue}>
      {children}
    </WorkflowContext.Provider>
  );
};
